import { ListItemIcon, ListItemText, MenuItem, debounce } from '@material-ui/core';
import { useCallback } from 'react';
import { css } from '@emotion/react';

import { MENTION_MARKUP } from '@jebel/constants';
import { createOrganizationMentionIdentifier } from '@jebel/utils';

import { createOrganizationFilter } from 'features/search/utils';
import { OrganizationItemFragment, useOrganizationsListLazyQuery } from 'shared/graphql';
import {
  MentionDataSource,
  MentionFetcher,
  MentionSuggestion,
  MentionSuggestionRenderer,
} from 'shared/features/mentions/types';
import { OrganizationAvatar } from 'shared/components/symbols';

const listItemCSS = css`
  max-width: 100%;

  &,
  span,
  p {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const renderSuggestion: MentionSuggestionRenderer<OrganizationItemFragment> = (
  suggestion,
  _search,
  _highlighted,
  index,
  focused,
) => {
  return (
    <MenuItem key={index} selected={focused}>
      <ListItemIcon>
        <OrganizationAvatar organization={suggestion.raw} />
      </ListItemIcon>

      <ListItemText css={listItemCSS} primary={suggestion.display} secondary="Business" />
    </MenuItem>
  );
};

/**
 * Create a `MentionDataSource` with the organizations data and configurations needed for `MentionsInput`.
 * @ticket https://8base-dev.atlassian.net/browse/JEB-1458
 * @returns Instance of `MentionDataSource`.
 */
export function useMentionsOrganizationDataSource(): MentionDataSource {
  const [fetchOrganizations] = useOrganizationsListLazyQuery();

  const fetchMentions: MentionFetcher = async (search, setSuggestions) => {
    const filter = createOrganizationFilter({ search });

    const response = await fetchOrganizations({
      variables: {
        filter,
        first: search.length > 3 ? undefined : 8,
      },
    });

    const organizations = response.data?.organizations.items ?? [];

    const suggestions = organizations.map<MentionSuggestion>(organization => {
      return {
        id: createOrganizationMentionIdentifier(organization.id),
        display: organization.name as string,
        raw: organization,
      };
    });

    setSuggestions(suggestions);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchMentionsDebounce = useCallback<MentionFetcher>(debounce(fetchMentions, 300), []);

  return {
    data: fetchMentionsDebounce,
    markup: MENTION_MARKUP,
    renderSuggestion,
  };
}
